import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const RETRY_COOLDOWN = 60; // seconds

const EmailVerificationBanner = () => {
  const { user, isEmailVerified, resendVerificationEmail } = useAuth();
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [sentAt, setSentAt] = useState(null);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  if (!user || isEmailVerified || !showBanner) {
    return null;
  }

  const handleResendEmail = async () => {
    setResending(true);
    setMessage(null);

    try {
      const { error } = await resendVerificationEmail();

      if (error) {
        setMessage({
          type: 'error',
          text: `Doğrulama e-postası gönderilemedi: ${error?.message || 'Bir hata oluştu'}`
        });
      } else {
        const now = new Date();
        setSentAt(now);
        setCountdown(RETRY_COOLDOWN);
        setMessage({ type: 'success' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Bir hata oluştu. Lütfen tekrar deneyin.'
      });
    } finally {
      setResending(false);
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${s?.toString()?.padStart(2, '0')}` : `${s}s`;
  };

  const progressPercent = sentAt ? ((RETRY_COOLDOWN - countdown) / RETRY_COOLDOWN) * 100 : 0;

  return (
    <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 md:p-6 mb-6 relative overflow-hidden">
      <button
        onClick={() => setShowBanner(false)}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors z-10"
        aria-label="Close banner"
      >
        <Icon name="X" size={20} />
      </button>
      {/* Success confirmation screen */}
      {message?.type === 'success' && sentAt ? (
        <div className="flex flex-col items-center text-center py-2">
          {/* Animated success icon */}
          <div className="relative mb-4">
            <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center animate-pulse">
              <div className="w-12 h-12 rounded-full bg-success/25 flex items-center justify-center">
                <Icon name="MailCheck" size={28} className="text-success" />
              </div>
            </div>
          </div>

          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            E-posta Gönderildi!
          </h3>
          <p className="text-sm text-muted-foreground mb-1">
            Doğrulama bağlantısı şu adrese gönderildi:
          </p>
          <p className="text-sm font-semibold text-foreground mb-4">
            {user?.email}
          </p>

          {/* Timestamp badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-success/10 border border-success/25 rounded-full text-xs text-success font-medium mb-5">
            <Icon name="Clock" size={13} />
            <span>Gönderim zamanı: {formatTime(sentAt)}</span>
          </div>

          {/* Retry timer section */}
          <div className="w-full max-w-xs">
            {countdown > 0 ? (
              <>
                {/* Progress bar */}
                <div className="w-full h-1.5 bg-muted rounded-full mb-2 overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-1000 ease-linear"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Tekrar göndermek için{' '}
                  <span className="font-semibold text-primary tabular-nums">
                    {formatCountdown(countdown)}
                  </span>{' '}
                  bekleyin
                </p>
              </>
            ) : (
              <button
                onClick={handleResendEmail}
                disabled={resending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                    <span>Gönderiliyor...</span>
                  </>
                ) : (
                  <>
                    <Icon name="RefreshCw" size={15} />
                    <span>Tekrar Gönder</span>
                  </>
                )}
              </button>
            )}
          </div>

          <p className="text-xs text-muted-foreground mt-4 max-w-xs">
            E-postayı göremiyorsanız spam/gereksiz klasörünüzü kontrol edin.
          </p>
        </div>
      ) : (
        /* Default banner */
        (<div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 bg-warning/20 rounded-lg">
            <Icon name="AlertCircle" size={24} className="text-warning" />
          </div>
          <div className="flex-1">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
              E-posta Adresinizi Doğrulayın
            </h3>

            <p className="text-sm md:text-base text-muted-foreground mb-4">
              Hesabınızın güvenliği için e-posta adresinizi doğrulamanız gerekmektedir.{' '}
              <span className="font-medium text-foreground">{user?.email}</span> adresine gönderilen doğrulama linkine tıklayın.
            </p>

            {message?.type === 'error' && (
              <div className="mb-4 p-3 rounded-lg text-sm bg-destructive/10 text-destructive border border-destructive/30">
                {message?.text}
              </div>
            )}

            <button
              onClick={handleResendEmail}
              disabled={resending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                  <span>Gönderiliyor...</span>
                </>
              ) : (
                <>
                  <Icon name="Mail" size={16} />
                  <span>Doğrulama E-postasını Tekrar Gönder</span>
                </>
              )}
            </button>
          </div>
        </div>)
      )}
    </div>
  );
};

export default EmailVerificationBanner;